import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, Camera, Sparkles, AlertCircle, Loader2, Download, 
  RefreshCw, X, Image as ImageIcon, ChevronLeft, ChevronRight,
  Heart, ShoppingBag, Phone, User, CheckCircle, Shield, Briefcase,
  Coffee, Moon, Plane, PartyPopper, ChevronDown
} from 'lucide-react';
import axios from 'axios';
import SEO, { SEO_CONFIGS } from '../../components/SEO';
import SizeRecommendPanel from '../../components/SizeRecommendPanel';
import { compressImageForUpload } from '../../utils/imageUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Constants
const MAX_SINGLE_IMAGE_SIZE_MB = 20;
const MAX_SINGLE_IMAGE_SIZE_BYTES = MAX_SINGLE_IMAGE_SIZE_MB * 1024 * 1024;

// Moment configurations
const MOMENT_CONFIG = {
  creative_workday: {
    label: 'Workday',
    icon: Briefcase,
    gradient: 'from-blue-500 to-indigo-600',
    light: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    description: 'Professional & stylish'
  },
  unwind: {
    label: 'Unwind',
    icon: Coffee,
    gradient: 'from-teal-500 to-emerald-600',
    light: 'bg-teal-50',
    text: 'text-teal-700',
    border: 'border-teal-200',
    description: 'Relaxed & comfortable'
  },
  after_hours: {
    label: 'Evening',
    icon: Moon,
    gradient: 'from-purple-500 to-blue-600',
    light: 'bg-purple-50',
    text: 'text-purple-700',
    border: 'border-purple-200',
    description: 'Glamorous nights'
  },
  escape: {
    label: 'Vacation',
    icon: Plane,
    gradient: 'from-orange-500 to-amber-600',
    light: 'bg-orange-50',
    text: 'text-orange-700',
    border: 'border-orange-200',
    description: 'Travel vibes'
  },
  gentle_celebration: {
    label: 'Celebration',
    icon: PartyPopper,
    gradient: 'from-blue-500 to-red-600',
    light: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    description: 'Special occasions'
  }
};

// ============ Fullscreen Gallery Component ============
const FullscreenGallery = ({ images, initialIndex, onClose, productName, onDownload }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const minSwipeDistance = 50;

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [onClose, goToPrev, goToNext]);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0) goToNext();
      else goToPrev();
    }
  };

  const handleDownload = async () => {
    const currentImage = images[currentIndex];
    if (!currentImage?.url) return;
    
    try {
      setIsDownloading(true);
      const response = await fetch(currentImage.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${currentImage.label || productName || 'image'}.jpg`.replace(/\s+/g, '-');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      window.open(currentImage.url, '_blank');
    } finally {
      setIsDownloading(false);
    }
  };

  const currentImage = images[currentIndex];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/95 flex flex-col"
      onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 text-white">
        <div className="flex-1">
          <span className="text-sm text-white/70">{currentIndex + 1} / {images.length}</span>
        </div>
        <h3 className="text-center font-medium truncate px-4">{currentImage?.label || productName}</h3>
        <div className="flex-1 flex justify-end gap-2">
          <button onClick={handleDownload} disabled={isDownloading}
            className="p-2 hover:bg-white/10 rounded-full disabled:opacity-50">
            {isDownloading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Download className="w-6 h-6" />}
          </button>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full">
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Image */}
      <div className="flex-1 flex items-center justify-center p-4 relative">
        {images.length > 1 && (
          <>
            <button onClick={goToPrev}
              className="absolute left-2 sm:left-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white z-10">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button onClick={goToNext}
              className="absolute right-2 sm:right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white z-10">
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
        
        <motion.img key={currentIndex}
          initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
          src={currentImage?.url} alt={currentImage?.label}
          className="max-w-full max-h-full object-contain rounded-lg"
        />
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="p-4 flex justify-center gap-2 overflow-x-auto">
          {images.map((img, idx) => (
            <button key={idx} onClick={() => setCurrentIndex(idx)}
              className={`w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all ${currentIndex === idx ? 'border-white scale-110' : 'border-transparent opacity-60'}`}>
              <img src={img.url} alt={img.label} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </motion.div>
  );
};

// ============ Customer Registration Component ============
const CustomerRegistration = ({ token, onComplete, productInfo }) => {
  const [step, setStep] = useState('register');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/public/customer/register?token=${token}`, {
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim() || null
      });

      if (response.data.verified) {
        onComplete({ sessionToken: response.data.session_token, customerPhone: phone, customerName: response.data.customer_name || name, customerEmail: email });
      } else {
        setStep('otp');
        setResendTimer(30);
      }
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      if (typeof errorDetail === 'object') {
        // Pydantic validation error
        setError('Please enter a valid 10-digit phone number');
      } else {
        setError(errorDetail || 'Failed to send OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/public/customer/verify-otp`, { phone, otp, token });
      onComplete({ sessionToken: response.data.session_token, customerPhone: phone, customerName: response.data.customer_name || name, customerEmail: email });
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/public/customer/resend-otp?phone=${phone}&token=${token}`);
      setResendTimer(30);
    } catch (err) {
      setError('Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'otp') {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl p-5 sm:p-6 shadow-lg border border-slate-100">
        <div className="text-center mb-5">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Shield className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800">Verify Your Phone</h3>
          <p className="text-slate-500 text-sm mt-1">Enter the OTP sent to +91 {phone}</p>
        </div>

        <form onSubmit={handleVerifyOTP} className="space-y-4">
          <input type="text" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="Enter OTP" className="w-full px-4 py-3 text-center text-xl tracking-widest border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500"
            maxLength={4} required />

          {error && <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg"><AlertCircle className="w-4 h-4" />{error}</div>}

          <button type="submit" disabled={loading || otp.length < 4}
            className="w-full py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:bg-slate-300 transition-colors flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
            Verify & Continue
          </button>

          <div className="text-center">
            <button type="button" onClick={handleResendOTP} disabled={resendTimer > 0 || loading}
              className="text-blue-600 hover:text-blue-700 text-sm disabled:text-slate-400">
              {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
            </button>
          </div>

          <button type="button" onClick={() => setStep('register')} className="w-full text-slate-500 text-sm hover:text-slate-700">
            ← Change phone number
          </button>
        </form>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl p-5 sm:p-6 shadow-lg border border-slate-100">
      
      {/* Product Preview Card - Single display of dress info */}
      {productInfo?.image && (
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl mb-5 border border-blue-100">
          <div className="w-16 h-20 sm:w-20 sm:h-24 rounded-lg overflow-hidden flex-shrink-0 border-2 border-white shadow-sm">
            <img 
              src={productInfo.image} 
              alt={productInfo.name || 'Product'} 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-blue-600 font-medium mb-0.5">You&apos;re trying on</p>
            <h4 className="font-semibold text-slate-800 text-sm sm:text-base truncate">{productInfo.name}</h4>
            {productInfo.price && (
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm font-bold text-slate-800">₹{productInfo.price.toLocaleString()}</span>
                {productInfo.mrp && productInfo.mrp > productInfo.price && (
                  <span className="text-xs text-slate-400 line-through">₹{productInfo.mrp.toLocaleString()}</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="text-center mb-5">
        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-3">
          <User className="w-6 h-6 text-white" />
        </div>
        <h3 className="text-lg font-semibold text-slate-800">Quick Registration</h3>
        <p className="text-slate-500 text-sm mt-1">Enter your details to try on this outfit</p>
      </div>

      <form onSubmit={handleRegister} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Your Name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Enter your name" className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500"
            required minLength={2} />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
          <div className="flex">
            <span className="px-3 py-3 bg-slate-100 border border-r-0 border-slate-200 rounded-l-xl text-slate-500">+91</span>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              placeholder="10-digit number" className="flex-1 px-4 py-3 border border-slate-200 rounded-r-xl focus:ring-2 focus:ring-blue-500"
              maxLength={10} required pattern="[0-9]{10}" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Email <span className="text-slate-400 font-normal">(optional)</span>
          </label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com" className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500" />
          <p className="text-xs text-slate-400 mt-1">We&apos;ll send your try-on results to your email</p>
        </div>

        {error && <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg"><AlertCircle className="w-4 h-4" />{error}</div>}

        <button type="submit" disabled={loading || name.length < 2 || phone.length < 10}
          className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-indigo-700 disabled:from-slate-400 disabled:to-slate-400 transition-all flex items-center justify-center gap-2">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Phone className="w-5 h-5" />}
          Get OTP & Continue
        </button>

        <p className="text-center text-xs text-slate-400">
          By continuing, you agree to our{' '}
          <a 
            href="/customer-terms" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-700 underline"
          >
            Terms & Conditions
          </a>
        </p>
      </form>
    </motion.div>
  );
};

// ============ Main PublicTryOn Component ============
const PublicTryOn = () => {
  const { token } = useParams();
  
  // Page data state
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Customer state
  const [customerVerified, setCustomerVerified] = useState(false);
  const [customerSession, setCustomerSession] = useState(null);
  
  // Try-on state
  const [humanImage, setHumanImage] = useState(null);
  const [humanImagePreview, setHumanImagePreview] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [tryonError, setTryonError] = useState(null);

  // Photo upload state (for decoupled size recommendation)
  const [customerPhotoS3Key, setCustomerPhotoS3Key] = useState(null);
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);
  const uploadSessionRef = useRef(0); // prevents stale upload from overwriting newer key
  
  // Moments state
  const [momentsExpanded, setMomentsExpanded] = useState(false);
  const [momentResults, setMomentResults] = useState({});
  const [generatingMoment, setGeneratingMoment] = useState(null);
  
  // UI state
  const [fullscreenGallery, setFullscreenGallery] = useState(null);
  const [liked, setLiked] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);
  const [buyInterestMessage, setBuyInterestMessage] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const [showSizeSelector, setShowSizeSelector] = useState(false);

  // Download image function - handles cross-origin images
  const handleDownloadImage = async (imageUrl, filename) => {
    try {
      setDownloading(true);
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'tryon-result.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      // Fallback: open in new tab
      window.open(imageUrl, '_blank');
    } finally {
      setDownloading(false);
    }
  };

  // Fetch page data
  useEffect(() => {
    const fetchPageData = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/public/tryon/${token}`);
        setPageData(response.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('This try-on link is not valid.');
        } else if (err.response?.status === 410) {
          setError(err.response?.data?.detail || 'This try-on link has expired or is no longer available.');
        } else {
          setError('Failed to load try-on page. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchPageData();
  }, [token]);

  const handleCustomerVerified = (session) => {
    setCustomerSession(session);
    setCustomerVerified(true);
  };

  // Background photo upload — fires immediately on image selection so the
  // size recommendation panel can appear without waiting for a TryOn.
  const uploadCustomerPhoto = useCallback(async (file, session) => {
    if (!session) return;
    const thisUpload = ++uploadSessionRef.current;
    setIsPhotoUploading(true);
    setCustomerPhotoS3Key(null);
    try {
      const formData = new FormData();
      formData.append('human_image', file);
      const response = await axios.post(
        `${API_URL}/api/public/tryon/${token}/upload-photo?session_token=${session.sessionToken}&customer_phone=${session.customerPhone}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000 }
      );
      if (thisUpload === uploadSessionRef.current) {
        setCustomerPhotoS3Key(response.data.customer_s3_key);
      }
    } catch (err) {
      // Upload failure is silent — size panel will show an appropriate error on submit
      console.warn('[PublicTryOn] Background photo upload failed:', err?.response?.data || err.message);
    } finally {
      if (thisUpload === uploadSessionRef.current) {
        setIsPhotoUploading(false);
      }
    }
  }, [token]);

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_SINGLE_IMAGE_SIZE_BYTES) {
      setTryonError(`Image size must be less than ${MAX_SINGLE_IMAGE_SIZE_MB}MB`);
      return;
    }

    // Compress to ≤ 1MB before storing and uploading
    const compressed = await compressImageForUpload(file);

    setHumanImage(compressed);
    setHumanImagePreview(URL.createObjectURL(compressed));
    setTryonError(null);
    setResult(null);
    setMomentResults({});
    setCustomerPhotoS3Key(null);

    // Fire background upload for size recommendation
    uploadCustomerPhoto(compressed, customerSession);
  };

  const handleTryOn = async () => {
    if (!humanImage || !customerSession) return;

    setProcessing(true);
    setTryonError(null);

    const formData = new FormData();
    formData.append('human_image', humanImage);

    try {
      const response = await axios.post(
        `${API_URL}/api/public/tryon/${token}?session_token=${customerSession.sessionToken}&customer_phone=${customerSession.customerPhone}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
      );

      setResult(response.data);
      setLiked(response.data.liked || false);
      setMomentsExpanded(true); // Auto-expand moments after successful try-on
    } catch (err) {
      if (err.response?.status === 402) {
        setTryonError('The store is currently out of credits. Please contact them.');
      } else if (err.response?.status === 429) {
        setTryonError('Too many requests. Please wait a moment and try again.');
      } else if (err.response?.status === 401) {
        setCustomerVerified(false);
        setCustomerSession(null);
        setTryonError('Session expired. Please verify your phone again.');
      } else {
        setTryonError(err.response?.data?.detail || 'Try-on failed. Please try again.');
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleGenerateMoment = async (momentType) => {
    if (!result?.session_id || generatingMoment) return;

    setGeneratingMoment(momentType);

    try {
      const response = await axios.post(
        `${API_URL}/api/public/tryon/${token}/moments/${result.session_id}?moment_type=${momentType}&session_token=${customerSession.sessionToken}&customer_phone=${customerSession.customerPhone}`
      );

      setMomentResults(prev => ({
        ...prev,
        [momentType]: response.data.image_url
      }));
    } catch (err) {
      console.error('Moment generation failed:', err);
      setTryonError(err.response?.data?.detail || 'Failed to generate moment. Please try again.');
    } finally {
      setGeneratingMoment(null);
    }
  };

  const handleLike = async () => {
    if (!result?.session_id || likeLoading) return;

    setLikeLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/public/tryon/${token}/like/${result.session_id}?session_token=${customerSession.sessionToken}&customer_phone=${customerSession.customerPhone}`
      );
      setLiked(response.data.liked);
      if (response.data.message) setBuyInterestMessage(response.data.message);
    } catch (err) {
      console.error('Failed to like:', err);
    } finally {
      setLikeLoading(false);
    }
  };

  const handleBuyNow = async () => {
    // Check if sizes are available and none selected
    if (pageData?.available_sizes?.length > 0 && !selectedSize) {
      setShowSizeSelector(true);
      return;
    }
    
    if (result?.session_id) {
      try {
        const response = await axios.post(
          `${API_URL}/api/public/tryon/${token}/buy-click/${result.session_id}?session_token=${customerSession.sessionToken}&customer_phone=${customerSession.customerPhone}`
        );
        
        if (response.data.buy_now_link) {
          // Append size parameter if selected
          let buyLink = response.data.buy_now_link;
          if (selectedSize) {
            const separator = buyLink.includes('?') ? '&' : '?';
            buyLink = `${buyLink}${separator}size=${encodeURIComponent(selectedSize)}`;
          }
          window.open(buyLink, '_blank');
          return;
        }
      } catch (err) {
        if (pageData?.buy_now_link) {
          let buyLink = pageData.buy_now_link;
          if (selectedSize) {
            const separator = buyLink.includes('?') ? '&' : '?';
            buyLink = `${buyLink}${separator}size=${encodeURIComponent(selectedSize)}`;
          }
          window.open(buyLink, '_blank');
          return;
        }
      }
      
      try {
        const interestResponse = await axios.post(
          `${API_URL}/api/public/tryon/${token}/buy-interest/${result.session_id}?session_token=${customerSession.sessionToken}&customer_phone=${customerSession.customerPhone}`
        );
        setBuyInterestMessage(interestResponse.data.message);
      } catch (err) {
        setBuyInterestMessage('We have captured your interest! Our team will connect with you soon.');
      }
    }
  };

  const resetTryOn = () => {
    setHumanImage(null);
    setHumanImagePreview(null);
    setResult(null);
    setTryonError(null);
    setLiked(false);
    setBuyInterestMessage(null);
    setMomentResults({});
    setMomentsExpanded(false);
    setCustomerPhotoS3Key(null);
    setIsPhotoUploading(false);
  };

  // Build gallery images
  const galleryImages = [];
  if (humanImagePreview) galleryImages.push({ url: humanImagePreview, label: 'Your Photo' });
  if (pageData?.tryon_input_image_url) galleryImages.push({ url: pageData.tryon_input_image_url, label: pageData.product_name });
  if (result?.output_image_url) galleryImages.push({ url: result.output_image_url, label: 'Try-On Result' });
  Object.entries(momentResults).forEach(([type, url]) => {
    if (url) galleryImages.push({ url, label: MOMENT_CONFIG[type]?.label || type });
  });

  // Get enabled moments
  const enabledMoments = pageData?.enabled_moments || [];
  const momentsEnabled = pageData?.enable_moments && enabledMoments.length > 0;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-blue-50">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading try-on experience...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-blue-50 p-4">
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg max-w-md text-center">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-7 h-7 text-red-500" />
          </div>
          <h2 className="text-lg font-semibold text-slate-800 mb-2">Oops!</h2>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  const momentTheme = MOMENT_CONFIG[pageData?.assigned_moment] || MOMENT_CONFIG.unwind;

  return (
    <>
      <SEO {...SEO_CONFIGS.publicTryOn} />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col">
      {/* Header - Always Blue Theme */}
      <header className="bg-gradient-to-r from-blue-600 to-indigo-600 py-4 sm:py-5 px-4 text-white shadow-lg sticky top-0 z-40">
        <div className="max-w-lg mx-auto">
          {/* Store Name with Logo */}
          <div className="flex items-center gap-2 mb-1">
            {pageData?.store_logo ? (
              <img 
                src={pageData.store_logo} 
                alt={pageData?.store_name || 'Store'} 
                className="w-8 h-8 rounded-full object-cover bg-white/20 border border-white/30"
              />
            ) : (
              <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                <span className="text-xs font-bold">{pageData?.store_name?.charAt(0) || 'S'}</span>
              </div>
            )}
            <span className="text-white/90 text-sm font-medium">{pageData?.store_name}</span>
          </div>
          {/* Purpose & Product */}
          <h1 className="text-lg sm:text-xl font-bold">Virtual Try-On Studio</h1>
          <p className="text-blue-100 text-sm mt-0.5">
            See how <span className="font-semibold text-white">{pageData?.product_name}</span> looks on you
            {pageData?.product_details?.selling_price && (
              <span className="ml-2 inline-flex items-center gap-1.5 bg-white/20 px-2 py-0.5 rounded-full text-white text-xs font-medium">
                ₹{pageData.product_details.selling_price.toLocaleString()}
                {pageData.product_details.mrp && pageData.product_details.mrp > pageData.product_details.selling_price && (
                  <span className="line-through text-white/60">₹{pageData.product_details.mrp.toLocaleString()}</span>
                )}
              </span>
            )}
          </p>
        </div>
      </header>

      <main className="max-w-lg mx-auto p-4 pb-8 space-y-4 flex-1">
        {/* Stock Notice */}
        {pageData?.stock_notice && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <p className="text-amber-700 text-sm">{pageData.stock_notice}</p>
          </div>
        )}

        {/* Customer Registration */}
        {!customerVerified && (
          <CustomerRegistration 
            token={token} 
            onComplete={handleCustomerVerified}
            productInfo={{
              name: pageData?.product_name,
              image: pageData?.tryon_input_image_url,
              description: pageData?.product_details?.description,
              price: pageData?.product_details?.selling_price,
              mrp: pageData?.product_details?.mrp
            }}
          />
        )}

        {/* Try-On Section */}
        {customerVerified && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-4 sm:p-5 shadow-lg border border-slate-100">
            
            {/* Always show side-by-side layout - Customer photo (empty or filled) and Garment */}
            <div className="space-y-4">
              {/* Image Preview Grid - Always visible */}
              <div className="grid grid-cols-2 gap-3">
                {/* Human Image / Upload Area */}
                <div className="relative">
                  <p className="text-xs font-medium text-slate-500 mb-1.5">Your Photo</p>
                  {!humanImagePreview ? (
                    <label className="block cursor-pointer">
                      <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-gradient-to-br from-slate-100 to-slate-50 border-2 border-dashed border-blue-300 hover:border-blue-400 hover:bg-blue-50/30 transition-all flex flex-col items-center justify-center">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                          <Camera className="w-6 h-6 text-blue-600" />
                        </div>
                        <p className="text-slate-600 font-medium text-sm">Tap to upload</p>
                        <p className="text-slate-400 text-xs mt-1">Full body photo</p>
                      </div>
                      <input type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
                    </label>
                  ) : (
                    <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-slate-100 cursor-pointer"
                      onClick={() => setFullscreenGallery({ images: galleryImages, index: 0 })}>
                      <img src={humanImagePreview} alt="Your photo" className="w-full h-full object-cover" />
                      {!result && (
                        <button onClick={(e) => { e.stopPropagation(); resetTryOn(); }}
                          className="absolute top-2 right-2 p-1.5 bg-black/50 rounded-full text-white hover:bg-black/70">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Result or Garment */}
                <div className="relative">
                  <p className="text-xs font-medium text-slate-500 mb-1.5">
                    {result ? 'Try-On Result' : 'Garment'}
                  </p>
                  <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-slate-100 cursor-pointer"
                    onClick={() => setFullscreenGallery({ images: galleryImages, index: result ? 2 : 1 })}>
                    <img src={result?.output_image_url || pageData?.tryon_input_image_url} 
                      alt={result ? 'Result' : 'Garment'} className="w-full h-full object-cover" />
                    {processing && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <div className="text-center text-white">
                          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                          <p className="text-sm">Creating magic...</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Pro Tips - Show only when no image uploaded */}
              {!humanImagePreview && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-amber-600 text-sm">💡</span>
                    <p className="font-medium text-amber-800 text-sm">Pro Tips for Best Results</p>
                  </div>
                  <ul className="text-xs text-amber-700 space-y-1 pl-5">
                    <li>• <strong>Just you:</strong> Single person photos work best</li>
                    <li>• <strong>Head to toe:</strong> Full body visible, head to feet</li>
                    <li>• <strong>Well-fitted clothes:</strong> Close-fitting outfits help AI understand your shape</li>
                    <li>• <strong>Good lighting:</strong> Natural daylight, avoid harsh shadows</li>
                    <li>• <strong>Face the camera:</strong> Stand comfortably, face forward</li>
                    <li>• <strong>Clear background:</strong> Remove distractions from frame</li>
                  </ul>
                </div>
              )}

              {/* Error */}
              {tryonError && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {tryonError}
                </div>
              )}

              {/* Actions */}
              {humanImagePreview && !result ? (
                <button onClick={handleTryOn} disabled={processing}
                  className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2">
                  {processing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                  {processing ? 'Creating Try-On...' : 'Try It On!'}
                </button>
              ) : result && (
                  <div className="space-y-3">
                    {/* Like & Buy Buttons */}
                    <div className="grid grid-cols-2 gap-3">
                      <button onClick={handleLike} disabled={likeLoading}
                        className={`py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${liked ? 'bg-red-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                        {likeLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Heart className={`w-5 h-5 ${liked ? 'fill-current' : ''}`} />}
                        {liked ? 'Liked!' : 'Like'}
                      </button>
                      <button onClick={handleBuyNow}
                        className="py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2">
                        <ShoppingBag className="w-5 h-5" />
                        Buy Now
                      </button>
                    </div>

                    {/* Size Selector */}
                    {showSizeSelector && pageData?.available_sizes?.length > 0 && (
                      <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="bg-slate-50 rounded-xl p-4 border border-slate-200"
                      >
                        <p className="text-sm font-medium text-slate-700 mb-3">Select Your Size</p>
                        <div className="flex flex-wrap gap-2">
                          {pageData.available_sizes.map((sizeInfo) => (
                            <button
                              key={sizeInfo.size}
                              onClick={() => {
                                if (sizeInfo.in_stock) {
                                  setSelectedSize(sizeInfo.size);
                                  setShowSizeSelector(false);
                                  // Trigger buy after size selection
                                  setTimeout(() => handleBuyNow(), 100);
                                }
                              }}
                              disabled={!sizeInfo.in_stock}
                              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                                selectedSize === sizeInfo.size
                                  ? 'bg-green-600 text-white'
                                  : sizeInfo.in_stock
                                    ? 'bg-white border border-slate-200 text-slate-700 hover:border-green-500 hover:text-green-600'
                                    : 'bg-slate-100 text-slate-400 cursor-not-allowed line-through'
                              }`}
                            >
                              {sizeInfo.size}
                              {!sizeInfo.in_stock && <span className="ml-1 text-xs">(Out)</span>}
                            </button>
                          ))}
                        </div>
                        {pageData.is_out_of_stock && (
                          <p className="text-xs text-red-500 mt-2">⚠️ {pageData.stock_notice || 'Currently out of stock'}</p>
                        )}
                      </motion.div>
                    )}

                    {/* Selected Size Display */}
                    {selectedSize && !showSizeSelector && (
                      <div className="flex items-center justify-between bg-green-50 rounded-lg px-3 py-2 border border-green-200">
                        <span className="text-sm text-green-700">
                          Selected Size: <strong>{selectedSize}</strong>
                        </span>
                        <button 
                          onClick={() => { setSelectedSize(null); setShowSizeSelector(true); }}
                          className="text-xs text-green-600 hover:text-green-700 underline"
                        >
                          Change
                        </button>
                      </div>
                    )}

                    {/* Buy Interest Message */}
                    {buyInterestMessage && (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <p className="text-green-700 text-sm">{buyInterestMessage}</p>
                      </div>
                    )}

                    {/* Download & Retry */}
                    <div className="flex gap-2">
                      <button 
                        onClick={() => handleDownloadImage(result.output_image_url, `tryon-${pageData?.product_name || 'result'}.jpg`)}
                        disabled={downloading}
                        className="flex-1 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 disabled:opacity-50 transition-colors flex items-center justify-center gap-2 text-sm">
                        {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        {downloading ? 'Downloading...' : 'Download'}
                      </button>
                      <button onClick={resetTryOn}
                        className="flex-1 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors flex items-center justify-center gap-2 text-sm">
                        <RefreshCw className="w-4 h-4" />
                        Try Another
                      </button>
                    </div>
                  </div>
                )}
              </div>
          </motion.div>
        )}

        {/* Size Recommendation Section — shows as soon as photo is uploaded, no TryOn needed */}
        {customerVerified && (customerPhotoS3Key || isPhotoUploading) && pageData?.size_chart && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            data-testid="size-recommend-section"
          >
            <SizeRecommendPanel
              apiFetcher={async (heightCm) => {
                if (!customerPhotoS3Key) {
                  throw new Error('Your photo is still uploading. Please wait a moment and try again.');
                }
                const response = await axios.post(
                  `${API_URL}/api/public/tryon/${token}/size-recommend?session_token=${customerSession.sessionToken}&customer_phone=${customerSession.customerPhone}`,
                  {
                    customer_s3_key: customerPhotoS3Key,
                    height_cm: heightCm,
                    size_chart_cm: pageData.size_chart
                  }
                );
                return response.data;
              }}
              productId={pageData.product_id}
              garment={pageData}
            />
          </motion.div>
        )}

        {/* Moments Section */}
        {customerVerified && result && momentsEnabled && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
            
            {/* Header */}
            <button onClick={() => setMomentsExpanded(!momentsExpanded)}
              className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-slate-800">See It In Action</p>
                  <p className="text-xs text-slate-500">Generate lifestyle photos in different settings</p>
                </div>
              </div>
              <motion.div animate={{ rotate: momentsExpanded ? 180 : 0 }}>
                <ChevronDown className="w-5 h-5 text-slate-400" />
              </motion.div>
            </button>

            {/* Content */}
            <AnimatePresence>
              {momentsExpanded && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                  className="border-t border-slate-100">
                  <div className="p-4 space-y-3">
                    {/* Moment Buttons */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {enabledMoments.map((momentType) => {
                        const config = MOMENT_CONFIG[momentType];
                        if (!config) return null;
                        const Icon = config.icon;
                        const hasResult = momentResults[momentType];
                        const isGenerating = generatingMoment === momentType;

                        return (
                          <button key={momentType} onClick={() => !hasResult && !isGenerating && handleGenerateMoment(momentType)}
                            disabled={isGenerating} 
                            className={`p-3 rounded-xl border-2 transition-all ${hasResult ? `${config.light} ${config.border}` : 'border-slate-200 hover:border-slate-300'} ${isGenerating ? 'opacity-70' : ''}`}>
                            <div className="flex flex-col items-center gap-1.5">
                              {isGenerating ? (
                                <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
                              ) : (
                                <Icon className={`w-5 h-5 ${hasResult ? config.text : 'text-slate-400'}`} />
                              )}
                              <span className={`text-xs font-medium ${hasResult ? config.text : 'text-slate-600'}`}>
                                {config.label}
                              </span>
                              {hasResult && <CheckCircle className="w-3 h-3 text-green-500" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Moment Results Gallery */}
                    {Object.keys(momentResults).length > 0 && (
                      <div className="mt-4">
                        <p className="text-xs font-medium text-slate-500 mb-2">Generated Moments</p>
                        <div className="grid grid-cols-3 gap-2">
                          {Object.entries(momentResults).map(([type, url]) => {
                            if (!url) return null;
                            const config = MOMENT_CONFIG[type];
                            const imageIndex = galleryImages.findIndex(img => img.url === url);
                            return (
                              <div key={type} className="relative cursor-pointer"
                                onClick={() => setFullscreenGallery({ images: galleryImages, index: imageIndex >= 0 ? imageIndex : 0 })}>
                                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-slate-100">
                                  <img src={url} alt={config?.label} className="w-full h-full object-cover" />
                                </div>
                                <div className={`absolute bottom-1 left-1 right-1 px-1.5 py-0.5 ${config?.light} rounded text-center`}>
                                  <span className={`text-[10px] font-medium ${config?.text}`}>{config?.label}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </main>

      {/* Nanokart.ai Footer with Disclaimer */}
      <footer className="bg-white border-t border-slate-200 py-4 mt-auto">
        <div className="max-w-lg mx-auto px-4">
          {/* AI Disclaimer */}
          <p className="text-center text-[10px] text-slate-400 mb-3 leading-relaxed">
            Try-on previews are AI-generated and may vary from real life. The AI may estimate hidden details to complete the look. Use as a helpful guide, not an exact representation.
          </p>
          
          {/* Link ID */}
          {pageData?.link_id && (
            <div className="flex items-center justify-center mb-2">
              <span className="text-xs text-slate-400">Link ID: </span>
              <span className="font-mono text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded ml-1">{pageData.link_id}</span>
            </div>
          )}
          
          <div className="flex items-center justify-center gap-2">
            <a 
              href="https://nanokart.ai" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-slate-500 hover:text-blue-600 transition-colors"
            >
              <span className="text-xs sm:text-sm">Powered by</span>
              <img 
                src="/new-logo.png" 
                alt="Nanokart.ai" 
                className="h-8 sm:h-10"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'inline';
                }}
              />
              <span className="font-semibold text-blue-600 hidden">Nanokart.ai</span>
            </a>
          </div>
          <p className="text-center text-xs text-slate-400 mt-1.5">
            ✨ Revolutionizing Online Shopping Experience
          </p>
        </div>
      </footer>

      {/* Fullscreen Gallery */}
      <AnimatePresence>
        {fullscreenGallery && (
          <FullscreenGallery
            images={fullscreenGallery.images}
            initialIndex={fullscreenGallery.index}
            onClose={() => setFullscreenGallery(null)}
            productName={pageData?.product_name}
          />
        )}
      </AnimatePresence>
    </div>
    </>
  );
};

export default PublicTryOn;
